import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Container,
  useMediaQuery,
  useTheme,
  Divider,
  alpha,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import VideocamIcon from '@mui/icons-material/Videocam';
import EmailIcon from '@mui/icons-material/Email';
import { company } from '../company';

// kind: 'router' = SPA link, 'anchor' = same-page section, 'external' = full navigation
const navItems = [
  { text: 'Hjem', to: '/', kind: 'router', icon: <HomeIcon /> },
  { text: 'Tjenester', to: '/#tjenester', kind: 'router', icon: <DesignServicesIcon /> },
  { text: 'Fiskekamera', to: '/fisk', kind: 'external', icon: <VideocamIcon /> },
] as const;

const Header: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location]);

  // Returns props for a nav target: full-page anchor for external paths
  // (the /fisk dashboard is a separate build), SPA link otherwise.
  const linkProps = (item: (typeof navItems)[number]): any =>
    item.kind === 'external'
      ? { component: 'a', href: item.to }
      : { component: RouterLink, to: item.to };

  const brand = (
    <Typography
      variant="h5"
      component={RouterLink}
      to="/"
      sx={{
        fontWeight: 800,
        letterSpacing: '-0.5px',
        textDecoration: 'none',
        color: 'text.primary',
      }}
    >
      Bjørnå <Box component="span" sx={{ color: 'primary.main' }}>IT</Box>
    </Typography>
  );

  const drawer = (
    <Box sx={{ width: 280 }} role="presentation">
      <Box sx={{ p: 3 }}>{brand}</Box>
      <Divider />
      <List sx={{ px: 1, pt: 1 }}>
        {navItems.map((item) => (
          <ListItemButton
            key={item.text}
            {...linkProps(item)}
            onClick={() => setDrawerOpen(false)}
            sx={{ borderRadius: 2, mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        ))}
        <ListItemButton
          component="a"
          href={`mailto:${company.email}`}
          sx={{ borderRadius: 2, mt: 1, bgcolor: alpha(theme.palette.primary.main, 0.08) }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
            <EmailIcon />
          </ListItemIcon>
          <ListItemText primary="Kontakt" />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        bgcolor: scrolled ? alpha('#ffffff', 0.85) : 'background.default',
        backdropFilter: scrolled ? 'blur(10px)' : 'none',
        borderBottom: scrolled ? `1px solid ${theme.palette.divider}` : '1px solid transparent',
        transition: 'all 0.3s ease',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ py: 1 }}>
          <Box sx={{ flexGrow: 1 }}>{brand}</Box>

          {isMobile ? (
            <>
              <IconButton edge="end" aria-label="meny" onClick={() => setDrawerOpen(true)}>
                <MenuIcon />
              </IconButton>
              <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                {drawer}
              </Drawer>
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {navItems.map((item) => (
                <Button
                  key={item.text}
                  {...linkProps(item)}
                  color="inherit"
                  sx={{
                    px: 2,
                    fontWeight: 500,
                    color: 'text.secondary',
                    '&:hover': { color: 'text.primary', bgcolor: 'transparent' },
                  }}
                >
                  {item.text}
                </Button>
              ))}
              <Button
                variant="contained"
                href={`mailto:${company.email}`}
                sx={{ ml: 1, borderRadius: 6, px: 3 }}
              >
                Kontakt
              </Button>
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
